import { useEffect } from 'react';

/**
 * Keeps the focused text input above the on-screen keyboard.
 *
 * Android shrinks the layout viewport when the keyboard opens, so the browser
 * reflows/scrolls the focused input into view for free. iOS Safari does NOT
 * resize the layout viewport — the keyboard overlays the content and only
 * `window.visualViewport` shrinks — and its native focus-scroll can't lift
 * inputs that live in fixed overlays or fixed-height nested scroll containers
 * (which is every typing surface in the calendar). This hook fills that gap.
 *
 * On focus and on visualViewport resize, if the focused input's bottom is below
 * the keyboard's top edge, it scrolls the input's nearest scrollable ancestor
 * (or the window) up by the overlap. It is capability-agnostic: on desktop and
 * on Android the keyboard doesn't cover the visual viewport (or the platform
 * already scrolled), so the overlap is <= 0 and it no-ops.
 *
 * Mount once (e.g. at a module root); it installs document-level listeners, so
 * it also covers inputs in portaled modals/overlays.
 */

const BOTTOM_MARGIN = 16;

const isTextEntry = (el) => {
  if (!el) return false;
  const { tagName } = el;
  if (tagName === 'TEXTAREA') return true;
  if (tagName === 'INPUT') {
    const type = (el.getAttribute('type') || 'text').toLowerCase();
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(type);
  }
  return el.isContentEditable;
};

const nearestScrollableAncestor = (el) => {
  let node = el.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const useKeyboardAwareInputScroll = () => {
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;

    const adjust = () => {
      const el = document.activeElement;
      if (!isTextEntry(el)) return;

      /** how much of the viewport bottom the keyboard is covering */
      const keyboardInset = window.innerHeight - (vv.height + vv.offsetTop);
      if (keyboardInset <= 1) return; // no keyboard covering (desktop / Android reflow)

      const rect = el.getBoundingClientRect();
      const visibleBottom = vv.offsetTop + vv.height;
      const overlap = rect.bottom - visibleBottom + BOTTOM_MARGIN;
      if (overlap <= 0) return; // already sitting above the keyboard

      const scroller = nearestScrollableAncestor(el);
      if (scroller) {
        scroller.scrollTop += overlap;
      } else {
        window.scrollBy(0, overlap);
      }
    };

    let focusTimer;
    const onFocusIn = (e) => {
      if (!isTextEntry(e.target)) return;
      /** wait for the keyboard to animate in / the viewport to settle; the
       * visualViewport resize below handles the keyboard-opening moment, this
       * covers focusing another field while the keyboard is already up */
      clearTimeout(focusTimer);
      focusTimer = setTimeout(adjust, 300);
    };

    document.addEventListener('focusin', onFocusIn);
    vv.addEventListener('resize', adjust);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('focusin', onFocusIn);
      vv.removeEventListener('resize', adjust);
    };
  }, []);
};

export default useKeyboardAwareInputScroll;
